import { type ReactNode } from 'react'
import { cn } from '../../helpers/classnames'
import { JosefButton } from '../buttons'
import { Skeleton } from '../../skeleton'

export interface JosefTableColumn {
  key: string
  title: string
  className?: string
}

// Standard pagination (offset/limit/page based - 0-indexed)
export interface JosefTableStandardPagination {
  type: 'standard'
  current: number
  total: number
  pageSize: number
  onPageChange: (page: number) => void
  itemName?: string
}

// Offset pagination (page/offset based - 1-indexed, typical Laravel style)
export interface JosefTableOffsetPagination {
  type: 'offset'
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  itemName?: string
}

// Cursor-based pagination
export interface JosefTableCursorPagination {
  type: 'cursor'
  prev?: string | null
  next?: string | null
  onPageChange: (cursor: string | null, direction: 'prev' | 'next') => void
  itemName?: string
}

export type JosefTablePaginationProps =
  | JosefTableStandardPagination
  | JosefTableOffsetPagination
  | JosefTableCursorPagination

export interface JosefTableProps {
  columns: JosefTableColumn[]
  children: ReactNode
  pagination?: JosefTablePaginationProps
  className?: string
  loading?: boolean
  loadingRows?: number
}

export function JosefTable({
  columns,
  children,
  pagination,
  className = '',
  loading = false,
  loadingRows = 10,
}: JosefTableProps) {
  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden flex flex-col',
        className
      )}
    >
      {/* Table */}
      <div className="flex-1 overflow-auto min-h-0 bg-white dark:bg-neutral-900">
        <table className="min-w-full divide-y divide-black/10 dark:divide-white/10">
          <thead className="bg-white dark:bg-neutral-900 sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    'px-4 py-3 text-left text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
                    column.className
                  )}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-900 divide-y divide-black/5 dark:divide-white/5 [&_tr]:transition-colors [&_td]:px-4 [&_td]:py-3 [&_td:first-child]:text-[14px] [&_td:first-child]:font-medium [&_td:first-child]:text-neutral-900 [&_td:first-child]:dark:text-white [&_td:not(:first-child):not(:last-child)]:text-[13px] [&_td:not(:first-child):not(:last-child)]:text-neutral-600 [&_td:not(:first-child):not(:last-child)]:dark:text-neutral-400">
            {loading
              ? // Skeleton rows when loading
                [...Array(loadingRows)].map((_, i) => (
                  <tr
                    key={i}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="whitespace-nowrap"
                      >
                        <Skeleton
                          className={cn(
                            'h-6 rounded-lg',
                            column.key === 'actions' ? 'w-16' : 'w-3/4'
                          )}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : children}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="mt-auto flex-shrink-0 border-t border-black/10 dark:border-white/10 bg-white dark:bg-neutral-900 px-4 py-3">
          <div className="flex items-center justify-between">
            {pagination.type === 'standard' ? (
              <>
                {/* Standard pagination - Left side: Page info */}
                <dl className="flex items-center gap-4">
                  <div>
                    <dt className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Page
                    </dt>
                    <dd className="text-[13px] font-medium text-gray-900 dark:text-white mt-0.5">
                      {pagination.current + 1} of{' '}
                      {Math.max(
                        1,
                        Math.ceil(pagination.total / pagination.pageSize)
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Total
                    </dt>
                    <dd className="text-[13px] font-medium text-gray-900 dark:text-white mt-0.5">
                      {pagination.total} {pagination.itemName || 'items'}
                    </dd>
                  </div>
                </dl>

                {/* Standard pagination - Right side: Page navigation */}
                <div className="flex items-center gap-1">
                  {/* Previous button */}
                  <JosefButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      pagination.onPageChange(
                        Math.max(0, pagination.current - 1)
                      )
                    }
                    disabled={pagination.current === 0}
                  >
                    Previous
                  </JosefButton>

                  {/* Page numbers */}
                  <div className="flex gap-1 mx-2">
                    {(() => {
                      const totalPages = Math.max(
                        1,
                        Math.ceil(pagination.total / pagination.pageSize)
                      )
                      const currentPage = pagination.current + 1
                      return Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let page
                          if (totalPages <= 5) {
                            page = i + 1
                          } else if (currentPage <= 3) {
                            page = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            page = totalPages - 4 + i
                          } else {
                            page = currentPage - 2 + i
                          }

                          return (
                            <JosefButton
                              key={page}
                              variant={
                                page === currentPage ? 'primary' : 'ghost'
                              }
                              size="sm"
                              onClick={() => pagination.onPageChange(page - 1)}
                              className="w-8 h-8 min-w-8"
                            >
                              {page}
                            </JosefButton>
                          )
                        }
                      )
                    })()}
                  </div>

                  {/* Next button */}
                  <JosefButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      pagination.onPageChange(pagination.current + 1)
                    }
                    disabled={
                      pagination.current >=
                      Math.max(
                        1,
                        Math.ceil(pagination.total / pagination.pageSize)
                      ) -
                        1
                    }
                  >
                    Next
                  </JosefButton>
                </div>
              </>
            ) : pagination.type === 'offset' ? (
              <>
                {/* Offset pagination - Left side: Page info */}
                <dl className="flex items-center gap-4">
                  <div>
                    <dt className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Page
                    </dt>
                    <dd className="text-[13px] font-medium text-gray-900 dark:text-white mt-0.5">
                      {pagination.currentPage} of {Math.max(1, pagination.totalPages)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Total
                    </dt>
                    <dd className="text-[13px] font-medium text-gray-900 dark:text-white mt-0.5">
                      {pagination.totalItems} {pagination.itemName || 'items'}
                    </dd>
                  </div>
                </dl>

                {/* Offset pagination - Right side: Page navigation */}
                <div className="flex items-center gap-1">
                  <JosefButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      pagination.onPageChange(Math.max(1, pagination.currentPage - 1))
                    }
                    disabled={pagination.currentPage <= 1}
                  >
                    Previous
                  </JosefButton>

                  <div className="flex gap-1 mx-2">
                    {(() => {
                      const totalPages = Math.max(1, pagination.totalPages)
                      const currentPage = pagination.currentPage
                      return Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let page
                          if (totalPages <= 5) {
                            page = i + 1
                          } else if (currentPage <= 3) {
                            page = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            page = totalPages - 4 + i
                          } else {
                            page = currentPage - 2 + i
                          }

                          return (
                            <JosefButton
                              key={page}
                              variant={page === currentPage ? 'primary' : 'ghost'}
                              size="sm"
                              onClick={() => pagination.onPageChange(page)}
                              className="w-8 h-8 min-w-8"
                            >
                              {page}
                            </JosefButton>
                          )
                        }
                      )
                    })()}
                  </div>

                  <JosefButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      pagination.onPageChange(pagination.currentPage + 1)
                    }
                    disabled={pagination.currentPage >= pagination.totalPages}
                  >
                    Next
                  </JosefButton>
                </div>
              </>
            ) : (
              <>
                {/* Cursor pagination - Left side: Item info */}
                <div>
                  <dt className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {pagination.itemName || 'Items'}
                  </dt>
                </div>

                {/* Cursor pagination - Right side: Prev/Next navigation */}
                <div className="flex items-center gap-2">
                  <JosefButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      pagination.onPageChange(pagination.prev, 'prev')
                    }
                    disabled={!pagination.prev}
                  >
                    Previous
                  </JosefButton>
                  <JosefButton
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      pagination.onPageChange(pagination.next, 'next')
                    }
                    disabled={!pagination.next}
                  >
                    Next
                  </JosefButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default JosefTable
